import React, { useState, useEffect, useRef } from 'react';

const ImageUploader = ({ onUploadComplete, currentUrl = '' }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(currentUrl);
    const [urlInput, setUrlInput] = useState('');
    const [uploadMode, setUploadMode] = useState('file');
    const fileInputRef = useRef(null);

    useEffect(() => {
        console.log('[ImageUploader] currentUrl changed:', currentUrl ? currentUrl.substring(0, 50) + '...' : 'empty');
        setPreviewUrl(currentUrl);
        if (currentUrl && !currentUrl.startsWith('data:')) {
            setUrlInput(currentUrl);
        }
    }, [currentUrl]);

    // Helper to check if file is video
    const isVideo = (urlOrFile) => {
        if (!urlOrFile) return false;
        if (typeof urlOrFile === 'string') {
            return urlOrFile.match(/\.(mp4|webm|mov)$/i) || urlOrFile.startsWith('data:video');
        }
        return urlOrFile.type.startsWith('video/');
    };

    const resizeImage = (file) => {
        return new Promise((resolve, reject) => {
            // IF VIDEO or GIF: Skip resize, read as DataURL directly
            if (file.type.startsWith('video/') || file.type === 'image/gif') {
                console.log('[ImageUploader] Video or GIF detected, skipping resize:', file.type);
                // D1 Limit is very strict (approx 1MB for safety)
                if (file.size > 1 * 1024 * 1024) { // 1MB Limit
                    alert('⚠️ VİDEO ÇOK BÜYÜK!\n\nVeritabanı sınırı nedeniyle sadece 1MB altındaki (çok kısa/küçük) videolar yüklenebilir.\n\nLütfen "Link ile Ekle" sekmesini kullanarak YouTube/Vimeo linki ekleyin veya videoyu küçültün.');
                    reject(new Error('Dosya çok büyük (Max 1MB). Lütfen Link seçeneğini kullanın.'));
                    return;
                }

                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = (e) => reject(new Error('Dosya okunamadı'));
                reader.readAsDataURL(file);
                return;
            }

            console.log('[ImageUploader] Starting resize for:', file.name, file.size, 'bytes');
            const reader = new FileReader();

            reader.onload = (event) => {
                console.log('[ImageUploader] FileReader loaded');
                const img = new Image();
                img.onload = () => {
                    console.log('[ImageUploader] Image loaded, dimensions:', img.width, 'x', img.height);
                    const canvas = document.createElement('canvas');
                    const MAX_SIZE = 800;
                    let width = img.width;
                    let height = img.height;

                    if (width > height && width > MAX_SIZE) {
                        height = (height * MAX_SIZE) / width;
                        width = MAX_SIZE;
                    } else if (height > MAX_SIZE) {
                        width = (width * MAX_SIZE) / height;
                        height = MAX_SIZE;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    console.log('[ImageUploader] Canvas converted, result length:', dataUrl.length);
                    resolve(dataUrl);
                };
                img.onerror = (err) => {
                    console.error('[ImageUploader] Image load error:', err);
                    reject(new Error('Görsel yüklenemedi'));
                };
                img.src = event.target.result;
            };

            reader.onerror = (err) => {
                console.error('[ImageUploader] FileReader error:', err);
                reject(new Error('Dosya okunamadı'));
            };

            reader.readAsDataURL(file);
        });
    };

    const handleFileSelect = async (e) => {
        console.log('[ImageUploader] handleFileSelect triggered');
        const file = e.target.files?.[0];
        if (!file) {
            console.log('[ImageUploader] No file selected');
            return;
        }

        console.log('[ImageUploader] File selected:', file.name, file.type, file.size);

        if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
            setError('Lütfen bir resim veya video dosyası seçin.');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const base64Url = await resizeImage(file);
            console.log('[ImageUploader] SUCCESS! Setting preview and calling onUploadComplete');
            setPreviewUrl(base64Url);
            onUploadComplete(base64Url);
        } catch (err) {
            console.error('[ImageUploader] Error:', err);
            setError(err.message || 'Dosya işlenirken bir hata oluştu.');
        } finally {
            setUploading(false);
            // Reset input so same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleUrlChange = (e) => {
        const val = e.target.value;
        setUrlInput(val);
        if (val === '' || val.startsWith('http')) {
            setPreviewUrl(val);
            onUploadComplete(val);
        }
    };

    const handleClear = () => {
        setPreviewUrl('');
        setUrlInput('');
        onUploadComplete('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const triggerFileSelect = () => {
        console.log('[ImageUploader] triggerFileSelect called, ref:', fileInputRef.current);
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div className="image-uploader-container" style={{ marginBottom: '20px' }}>
            <div className="mode-tabs" style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                <button
                    type="button"
                    onClick={() => setUploadMode('file')}
                    style={{
                        flex: 1, padding: '8px', cursor: 'pointer',
                        border: '1px solid #ddd', borderRadius: '4px',
                        background: uploadMode === 'file' ? '#f39c12' : '#fff',
                        color: uploadMode === 'file' ? '#fff' : '#333',
                        fontWeight: 'bold'
                    }}
                >
                    📁 Dosya Yükle
                </button>
                <button
                    type="button"
                    onClick={() => setUploadMode('url')}
                    style={{
                        flex: 1, padding: '8px', cursor: 'pointer',
                        border: '1px solid #ddd', borderRadius: '4px',
                        background: uploadMode === 'url' ? '#f39c12' : '#fff',
                        color: uploadMode === 'url' ? '#fff' : '#333',
                        fontWeight: 'bold'
                    }}
                >
                    🔗 Link ile Ekle
                </button>
            </div>

            <div className="uploader-box" style={{ border: '2px dashed #ddd', borderRadius: '12px', padding: '20px', textAlign: 'center', background: '#fdfdfd' }}>
                {uploadMode === 'file' ? (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,video/mp4,video/webm"
                            onChange={handleFileSelect}
                            disabled={uploading}
                            style={{ display: 'none' }}
                        />
                        <div onClick={triggerFileSelect} style={{ cursor: 'pointer', color: '#666' }}>
                            <div style={{ fontSize: '32px', marginBottom: '10px' }}>🖼️ 🎥</div>
                            <p style={{ margin: 0, fontWeight: '500' }}>{previewUrl ? 'Medyayı Değiştir' : 'Resim veya Video Yüklemek İçin Tıklayın'}</p>
                            <p style={{ fontSize: '11px', color: '#999', marginTop: '5px' }}>JPG, PNG (Otomatik Sıkıştırılır) | GIF, MP4 (Max 1MB !!)</p>
                        </div>
                    </>
                ) : (
                    <div className="url-input-box">
                        <input
                            type="text"
                            placeholder="https://example.com/image.jpg"
                            value={urlInput}
                            onChange={handleUrlChange}
                            style={{
                                width: '100%', padding: '10px', border: '1px solid #ddd',
                                borderRadius: '6px', fontSize: '14px', marginBottom: '5px',
                                boxSizing: 'border-box'
                            }}
                        />
                        <p style={{ fontSize: '11px', color: '#999', margin: 0 }}>Dış bağlantı (link) yapıştırın</p>
                    </div>
                )}

                {previewUrl && (
                    <div className="preview-container" style={{ marginTop: '20px', position: 'relative', display: 'inline-block' }}>
                        {isVideo(previewUrl) ? (
                            <video
                                src={previewUrl}
                                controls
                                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #eee' }}
                            />
                        ) : (
                            <img
                                src={previewUrl}
                                alt="Önizleme"
                                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #eee' }}
                                onError={() => setError('Medya yüklenemedi. Lütfen linki kontrol edin.')}
                                onLoad={() => setError(null)}
                            />
                        )}

                        <button
                            type="button"
                            onClick={handleClear}
                            style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '50%', width: '25px', height: '25px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', zIndex: 10 }}
                        >
                            ✕
                        </button>
                    </div>
                )}

                {uploading && <p style={{ color: '#f39c12', fontWeight: 'bold', marginTop: '10px', fontSize: '13px' }}>⏳ Medya işleniyor...</p>}
                {error && <p style={{ color: '#e74c3c', marginTop: '10px', fontSize: '13px' }}>{error}</p>}
            </div>
        </div>
    );
};

export default ImageUploader;

